/* global DecompressionStream, Response, TextDecoder */
// ─── La factura electrónica del PROVEEDOR, leída de su XML ──────────────────
// Lo que llega al correo es un ZIP con el PDF y un XML. Ese XML es el
// «contenedor» de la DIAN (AttachedDocument): adentro, como texto, va la
// factura firmada (Invoice, UBL 2.1) y la respuesta de validación de la DIAN
// (ApplicationResponse). De ahí sale todo lo que hoy se teclea al causar: el
// NIT y el nombre del proveedor, el número, la fecha, el vencimiento, el CUFE,
// cada línea con su cantidad, precio e IVA, y los totales.
//
// Es puro (sin estado ni DOM): las pruebas lo leen con el XML real de STOCKSUR
// (FVE66868). El cruce con las órdenes de compra también vive aquí.
import { buscarTodos, hijo, hijos, parseXML, texto } from "./xmlMini.js";

const num = (s) => { const n = Number(String(s ?? "").trim()); return Number.isFinite(n) ? n : 0; };
const red2 = (n) => Math.round(n * 100) / 100;
const soloDigitos = (s) => String(s || "").replace(/\D/g, "");

// Los NIT se comparan sin puntos, guiones ni dígito de verificación.
export function nitBase(nit) {
  const t = String(nit || "").trim();
  const conGuion = t.match(/^([\d.\s]+)-\s*\d$/);
  return soloDigitos(conGuion ? conGuion[1] : t);
}

// Códigos de tributo de la DIAN (tabla 13.2.2) que aparecen en una compra.
const TRIBUTOS = { "01": "IVA", "02": "IC", "03": "ICA", "04": "INC", "05": "ReteIVA", "06": "ReteFuente", "07": "ReteICA", "ZZ": "Otro" };

function impuestosDe(nodoTaxTotal) {
  return hijos(nodoTaxTotal, "TaxSubtotal").map(st => {
    const id = texto(st, "TaxCategory", "TaxScheme", "ID");
    return {
      codigo: id, tributo: TRIBUTOS[id] || texto(st, "TaxCategory", "TaxScheme", "Name") || id,
      base: num(texto(st, "TaxableAmount")), pct: num(texto(st, "TaxCategory", "Percent") || texto(st, "Percent")),
      valor: num(texto(st, "TaxAmount"))
    };
  });
}

function parteDe(party) {
  if (!party) return null;
  const pts = hijo(party, "PartyTaxScheme");
  const ple = hijo(party, "PartyLegalEntity");
  const companyId = hijo(pts, "CompanyID") || hijo(ple, "CompanyID") || hijo(party, "PartyIdentification", "ID");
  const dir = hijo(party, "PhysicalLocation", "Address") || hijo(pts, "RegistrationAddress");
  // schemeName 31 = NIT, 13 = cédula; con NIT, schemeID trae el dígito de verificación.
  const tipoDoc = companyId ? (companyId.attrs.schemeName || "") : "";
  return {
    nit: soloDigitos(companyId ? companyId.text : ""),
    dv: tipoDoc === "31" && companyId ? (companyId.attrs.schemeID || "") : "",
    tipoDoc,
    nombre: (texto(pts, "RegistrationName") || texto(ple, "RegistrationName") || texto(party, "PartyName", "Name")).replace(/\s+/g, " ").trim(),
    email: texto(party, "Contact", "ElectronicMail"),
    telefono: texto(party, "Contact", "Telephone"),
    ciudad: texto(dir, "CityName"),
    direccion: texto(dir, "AddressLine", "Line"),
    responsabilidades: texto(pts, "TaxLevelCode")
  };
}

const RAICES = { Invoice: "factura", CreditNote: "nota_credito", DebitNote: "nota_debito" };
const LINEAS = { Invoice: ["InvoiceLine", "InvoicedQuantity"], CreditNote: ["CreditNoteLine", "CreditedQuantity"], DebitNote: ["DebitNoteLine", "DebitedQuantity"] };

// El documento de la DIAN que viaja como texto dentro del contenedor.
function incrustado(doc, etiquetas) {
  for (const d of buscarTodos(doc, "Description")) {
    const t = d.text.trim();
    if (t.startsWith("<") && etiquetas.some(e => new RegExp("<([A-Za-z]+:)?" + e + "[\\s>]").test(t))) return parseXML(t);
  }
  return null;
}
const raizDe = (doc) => doc.children.find(c => c.name && c.name !== "#documento") || null;

// Lee el XML (contenedor o factura suelta) y devuelve la factura, o lanza un
// error que se le puede mostrar a la persona tal cual.
export function leerFacturaXML(xmlTexto) {
  let doc;
  try { doc = parseXML(String(xmlTexto || "")); }
  catch (e) { throw new Error("El archivo no se pudo leer como XML: " + e.message); }
  let raiz = raizDe(doc);
  if (!raiz) throw new Error("El archivo está vacío o no es un XML.");
  let validacion = null;
  if (raiz.name === "AttachedDocument") {
    const interno = incrustado(doc, Object.keys(RAICES));
    if (!interno) throw new Error("El contenedor de la DIAN no trae la factura adentro.");
    const ar = incrustado(doc, ["ApplicationResponse"]);
    if (ar) {
      const resp = hijo(raizDe(ar), "DocumentResponse", "Response");
      validacion = { codigo: texto(resp, "ResponseCode"), descripcion: texto(resp, "Description") };
    }
    raiz = raizDe(interno);
  }
  const tipo = RAICES[raiz.name];
  if (!tipo) throw new Error(`El archivo no es una factura electrónica de la DIAN (trae «${raiz.name}»).`);

  const [etqLinea, etqCant] = LINEAS[raiz.name];
  const lineas = hijos(raiz, etqLinea).map(l => {
    const cantN = hijo(l, etqCant);
    const cantidad = num(cantN ? cantN.text : 0);
    const subtotal = num(texto(l, "LineExtensionAmount"));
    const imp = hijos(l, "TaxTotal").flatMap(impuestosDe);
    const iva = imp.filter(x => x.codigo === "01");
    const item = hijo(l, "Item");
    return {
      linea: texto(l, "ID"),
      descripcion: hijos(item, "Description").map(d => d.text.trim()).filter(Boolean).join(" ").replace(/\s+/g, " "),
      codigo: texto(item, "SellersItemIdentification", "ID") || texto(item, "StandardItemIdentification", "ID"),
      cantidad, unidad: cantN ? (cantN.attrs.unitCode || "") : "",
      // El precio por unidad sale del valor de la línea: muchos facturadores
      // llenan mal BaseQuantity (STOCKSUR pone 300 con un precio unitario de
      // 6.900), y lo que cuadra con la DIAN es la línea.
      precioUnitario: cantidad > 0 ? red2(subtotal / cantidad) : num(texto(l, "Price", "PriceAmount")),
      subtotal,
      ivaPct: iva.length ? iva[0].pct : 0,
      iva: iva.reduce((s, x) => s + x.valor, 0),
      otrosImpuestos: imp.filter(x => x.codigo !== "01").reduce((s, x) => s + x.valor, 0),
      regalo: texto(l, "FreeOfChargeIndicator") === "true"
    };
  });

  const impuestos = hijos(raiz, "TaxTotal").flatMap(impuestosDe);
  const retenciones = hijos(raiz, "WithholdingTaxTotal").flatMap(impuestosDe);
  const lmt = hijo(raiz, "LegalMonetaryTotal");
  const prefijo = texto(buscarTodos(raiz, "Prefix")[0] || null);
  const vence = texto(raiz, "PaymentMeans", "PaymentDueDate") || texto(raiz, "DueDate");
  const ref = hijo(raiz, "BillingReference", "InvoiceDocumentReference");

  return {
    tipo,
    numero: texto(raiz, "ID"),
    prefijo,
    cufe: texto(raiz, "UUID"),
    fecha: texto(raiz, "IssueDate"),
    vence,
    // 1 = contado, 2 = crédito (tabla 13.3.4.1).
    formaPago: texto(raiz, "PaymentMeans", "ID") === "1" ? "contado" : "credito",
    moneda: texto(raiz, "DocumentCurrencyCode") || "COP",
    proveedor: parteDe(hijo(raiz, "AccountingSupplierParty", "Party")),
    adquirente: parteDe(hijo(raiz, "AccountingCustomerParty", "Party")),
    lineas,
    totales: {
      subtotal: num(texto(lmt, "LineExtensionAmount")),
      base: num(texto(lmt, "TaxExclusiveAmount")),
      iva: impuestos.filter(x => x.codigo === "01").reduce((s, x) => s + x.valor, 0),
      otrosImpuestos: impuestos.filter(x => x.codigo !== "01").reduce((s, x) => s + x.valor, 0),
      descuentos: num(texto(lmt, "AllowanceTotalAmount")),
      cargos: num(texto(lmt, "ChargeTotalAmount")),
      anticipos: num(texto(lmt, "PrepaidAmount")),
      total: num(texto(lmt, "TaxInclusiveAmount")),
      aPagar: num(texto(lmt, "PayableAmount"))
    },
    impuestos, retenciones,
    notas: hijos(raiz, "Note").map(n => n.text.trim()).filter(Boolean),
    // La nota crédito dice a qué factura reversa.
    facturaReferida: ref ? { numero: texto(ref, "ID"), cufe: texto(ref, "UUID"), fecha: texto(ref, "IssueDate") } : null,
    validacion,
    validadaDian: validacion ? validacion.codigo === "02" : null
  };
}

// ─── El ZIP que llega al correo ─────────────────────────────────────────────
// Lector mínimo: el directorio central dice dónde está cada archivo, y lo
// comprimido se infla con DecompressionStream (navegador y Node 18+).
async function inflar(bytes) {
  const ds = new DecompressionStream("deflate-raw");
  const flujo = new Blob([bytes]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(flujo).arrayBuffer());
}

export function textoDeBytes(bytes) {
  const cabeza = new TextDecoder("latin1").decode(bytes.slice(0, 200));
  const enc = (cabeza.match(/encoding=["']([^"']+)["']/i) || [])[1] || "utf-8";
  try { return new TextDecoder(/8859|latin/i.test(enc) ? "latin1" : "utf-8").decode(bytes); }
  catch { return new TextDecoder("utf-8").decode(bytes); }
}

export async function archivosDelZip(buffer) {
  const b = new Uint8Array(buffer);
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  let fin = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65557); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { fin = i; break; }
  }
  if (fin < 0) throw new Error("El ZIP está dañado o no es un ZIP.");
  const total = dv.getUint16(fin + 10, true);
  let p = dv.getUint32(fin + 16, true);
  const salida = [];
  for (let k = 0; k < total; k++) {
    if (dv.getUint32(p, true) !== 0x02014b50) throw new Error("El ZIP está dañado.");
    const metodo = dv.getUint16(p + 10, true);
    const comprimido = dv.getUint32(p + 20, true);
    const largoNombre = dv.getUint16(p + 28, true), largoExtra = dv.getUint16(p + 30, true), largoComentario = dv.getUint16(p + 32, true);
    const local = dv.getUint32(p + 42, true);
    const nombre = new TextDecoder("utf-8").decode(b.slice(p + 46, p + 46 + largoNombre));
    p += 46 + largoNombre + largoExtra + largoComentario;
    if (nombre.endsWith("/")) continue;
    const inicio = local + 30 + dv.getUint16(local + 26, true) + dv.getUint16(local + 28, true);
    const datos = b.slice(inicio, inicio + comprimido);
    if (metodo !== 0 && metodo !== 8) continue; // otro método: no es lo que manda un facturador
    salida.push({ nombre, bytes: metodo === 0 ? datos : await inflar(datos) });
  }
  return salida;
}

// Del archivo que la persona suelta (XML o ZIP) a la factura leída.
export async function leerArchivoFactura(nombre, buffer) {
  const b = new Uint8Array(buffer);
  const esZip = b[0] === 0x50 && b[1] === 0x4b;
  if (!esZip) return leerFacturaXML(textoDeBytes(b));
  const xmls = (await archivosDelZip(buffer)).filter(a => /\.xml$/i.test(a.nombre));
  if (!xmls.length) throw new Error(`El ZIP «${nombre}» no trae ningún XML (solo trae el PDF). Pídele al proveedor el XML o busca el ZIP completo en el correo.`);
  let ultimo = null;
  for (const a of xmls) {
    try { return leerFacturaXML(textoDeBytes(a.bytes)); } catch (e) { ultimo = e; }
  }
  throw ultimo;
}

// ─── El cruce con las órdenes de compra ─────────────────────────────────────
// Cada línea del XML busca la línea de OCP que le corresponde. El nombre nunca
// es igual («BOTELLA SILLY NEGRO» en la factura, «Botella SILLY» en la orden),
// así que se mide cuántas palabras del artículo de la orden aparecen en la
// descripción de la factura, y el precio y la cantidad desempatan.
const palabras = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
  .split(/[^a-z0-9]+/).filter(w => w.length >= 2);

export function parecido(descFactura, articuloOrden) {
  const f = new Set(palabras(descFactura));
  const o = palabras(articuloOrden);
  if (!o.length || !f.size) return 0;
  return o.filter(w => f.has(w)).length / o.length;
}

// candidatas: [{ ocpId, idx, articulo, pendiente, precio }]. Devuelve, por cada
// línea del XML, la candidata elegida (o null) con su puntaje.
export function cruzarLineas(lineasXML, candidatas) {
  const pares = [];
  lineasXML.forEach((l, li) => candidatas.forEach((c, ci) => {
    const nombre = parecido(l.descripcion, c.articulo);
    const mismoPrecio = c.precio > 0 && Math.abs(l.precioUnitario - c.precio) / c.precio <= 0.005;
    const mismaCant = Math.abs(l.cantidad - c.pendiente) < 0.001;
    // Con la mitad de las palabras no alcanza: «BOTELLA SILLY NEGRO» comparte
    // «botella» con «Botella Belize» (otra orden de STOCKSUR). Hace falta casi
    // todo el nombre, o la mitad más el mismo precio o la misma cantidad, o el
    // mismo precio y al menos una palabra.
    const acepta = nombre >= 0.66 || (nombre >= 0.5 && (mismoPrecio || mismaCant)) || (mismoPrecio && nombre > 0);
    if (!acepta) return;
    pares.push({ li, ci, puntaje: nombre + (mismoPrecio ? 0.5 : 0) + (mismaCant ? 0.25 : 0) });
  }));
  pares.sort((a, b) => b.puntaje - a.puntaje);
  const usadaL = new Set(), usadaC = new Set();
  const salida = lineasXML.map(() => null);
  for (const p of pares) {
    if (usadaL.has(p.li) || usadaC.has(p.ci)) continue;
    usadaL.add(p.li); usadaC.add(p.ci);
    salida[p.li] = { ...candidatas[p.ci], puntaje: p.puntaje };
  }
  return salida;
}
